import type {
  ExperiencePort,
  ExperienceConnection,
  TalkActionIntent,
  TextTurnIntent,
  ExperienceActionIntent,
  BootstrapBindings,
  ActionResponse,
  MissionUpdate,
  SimEvent,
  WorldUpdate,
  AreaOfInterest,
  CharacterPresenceProjection,
  CharacterPresenceState,
  CharacterConversationRole,
  Utterance,
  UtteranceResponse,
  ExperienceConfig,
  ExperienceEventsBatch,
} from '../../../ports/ExperiencePort';
import {
  parsePresentationDirectives,
  PRESENTATION_DIRECTIVES_FIELD, allowedMethodsFromVocabulary } from '../../../application/experience/presentationDirectives';
import type { ErrorFrame } from '../../../ports/ErrorFrame';
import {
  isConnectedFrame,
  isActionResponseFrame,
  isErrorFrame,
  isMissionUpdateFrame,
  isSimEventFrame,
  isWorldUpdateFrame,
  isAreaOfInterestFrame,
  isUtteranceFrame,
  isConfigFrame,
  isWireFrame,
  isWireActionFrame,
  type WireFrame,
  type ConnectedFrame,
  type ActionResponseFrame,
  type MissionUpdateFrame,
  type SimEventFrame,
  type WorldUpdateFrame,
  type AreaOfInterestFrame,
  type UtteranceFrame,
  type ConfigFrame,
} from '../wire/WireSchemas';
import { gaiaWebSocketUrl } from '../url/GaiaUrls';

const READY_BOOTSTRAP_STATUSES = new Set([
  'provisioned',
  'resumed',
  'started_new',
  'attached_existing',
]);

// source.response is Gaia PR #1030's step answer contract. Logged at most
// once per module lifetime, so a flaky step definition does not spam the
// console on every malformed entry line.
let hasWarnedAboutMalformedResponse = false;


const PING_INTERVAL = 30_000;
const RECONNECT_BASE_DELAY = 1000;
const RECONNECT_MAX_DELAY = 30_000;

export class GaiaExperienceWebSocket implements ExperiencePort {
  private ws: WebSocket | null = null;
  private handlers: ExperienceConnection | null = null;
  private allowedPresentationMethods: ReadonlySet<string> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private currentToken = '';
  private currentExperienceId = '';
  private currentBindings: BootstrapBindings | null = null;

  /** Last position known to this connection; sent with every (re)connect so Gaia can choose a location-based mission. */
  private lastPosition: { latitude: number; longitude: number } | null = null;

  /**
   * An explicit run is a server-authorized attachment hint, never mission authority.
   * `locate` reads the phone's position before the first connect; Gaia decides what it means.
   */
  constructor(
    private baseUrl: string,
    private options: {
      requestedSessionId?: string;
      locate?: () => Promise<{ latitude: number; longitude: number } | null>;
    } = {}
  ) {}

  connect(token: string, experienceId: string, handlers: ExperienceConnection): void {
    this.allowedPresentationMethods = null;
    this.currentToken = token;
    this.currentExperienceId = experienceId;
    this.handlers = handlers;
    const locate = this.options.locate;
    if (!locate || this.lastPosition) {
      this.doConnect();
      return;
    }
    void locate()
      .catch(() => null)
      .then((position) => {
        if (position) this.lastPosition = position;
        if (this.handlers === handlers) this.doConnect();
      });
  }

  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    const ws = this.ws;
    this.ws = null;
    this.handlers = null;
    this.currentBindings = null;
    this.reconnectAttempts = 0;
    ws?.close(1000);
  }

  updateToken(token: string): void {
    this.currentToken = token;
  }

  sendTalk(action: TalkActionIntent): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(toWireActionFrame(action)));
    }
  }

  sendTextTurn(turn: TextTurnIntent): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          type: 'text_turn',
          session_id: turn.sessionId,
          text: turn.text,
          request_id: turn.requestId,
          idempotency_key: turn.idempotencyKey,
        })
      );
    }
  }

  sendAction(action: ExperienceActionIntent): void {
    const frame = toWireActionFrame(action);
    if (!isWireActionFrame(frame)) {
      this.handlers?.onError({
        type: 'error',
        message: 'Invalid outbound action frame shape',
        code: 'invalid_outbound_action',
      });
      return;
    }

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(frame));
    }
  }

  sendPing(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'ping' }));
    }
  }

  sendRequestConfig(sessionId: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'experience.request_config', session_id: sessionId }));
    }
  }

  sendEvents(batch: ExperienceEventsBatch): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          type: 'experience.events',
          session_id: batch.sessionId,
          events: batch.events.map((event) => ({
            event_id: event.eventId,
            event_type: event.eventType,
            occurred_at: event.occurredAt,
            payload: event.payload ?? {},
          })),
        })
      );
    }
  }

  sendLocation(lat: number, lng: number, accuracy?: number): void {
    this.lastPosition = { latitude: lat, longitude: lng };
    const requestId = crypto.randomUUID();
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          type: 'update_location',
          ...(this.currentBindings ? { session_id: this.currentBindings.sessionId } : {}),
          request_id: requestId,
          idempotency_key: requestId,
          // Send-time observation only; physical acquisition-age policy is separate.
          observed_at: new Date().toISOString(),
          lat,
          lng,
          accuracy,
          timestamp: Date.now(),
        })
      );
    }
  }

  private doConnect(): void {
    const attachment = this.options.requestedSessionId
      ? `&run_intent=attach_existing&requested_session_id=${encodeURIComponent(this.options.requestedSessionId)}&lifecycle_operation_id=${crypto.randomUUID()}`
      : '';
    const url = `${gaiaWebSocketUrl(this.baseUrl, '/ws/experience')}?token=${encodeURIComponent(
      this.currentToken
    )}&experience=${encodeURIComponent(this.currentExperienceId)}${attachment}${
      this.lastPosition
        ? `&latitude=${encodeURIComponent(this.lastPosition.latitude)}&longitude=${encodeURIComponent(this.lastPosition.longitude)}`
        : ''
    }`;

    const socket = new WebSocket(url);
    this.ws = socket;

    this.ws.onopen = () => {
      if (this.ws !== socket) return;
      this.reconnectAttempts = 0;
      this.startPing();
    };

    this.ws.onmessage = (event: MessageEvent) => {
      if (this.ws !== socket) return;
      try {
        const parsed = JSON.parse(event.data) as unknown;
        if (!isWireFrame(parsed)) {
          this.handlers?.onError({ type: 'error', message: 'Invalid server frame shape' });
          return;
        }
        const frame = parsed as WireFrame;
        this.handleFrame(frame);
      } catch {
        this.handlers?.onError({ type: 'error', message: 'Failed to parse server frame' });
      }
    };

    this.ws.onclose = (event: CloseEvent) => {
      // A socket that was replaced is not the connection any more; its late
      // close must not touch the new one's ping or start a second reconnect.
      if (this.ws !== socket) return;
      this.stopPing();
      const reason = `WebSocket closed: code=${event.code} reason=${event.reason}`;
      this.handlers?.onDisconnected(reason);

      if (this.handlers && event.code !== 1000) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = () => {
      if (this.ws !== socket) return;
      this.handlers?.onError({ type: 'error', message: 'WebSocket connection error' });
    };
  }

  private handleFrame(frame: WireFrame): void {
    if (isConnectedFrame(frame)) {
      this.handleConnected(frame);
    } else if (isActionResponseFrame(frame)) {
      this.handleActionResponse(frame);
    } else if (isErrorFrame(frame)) {
      this.handleError(frame);
    } else if (isMissionUpdateFrame(frame)) {
      this.handlers?.onMissionUpdate(frame as unknown as MissionUpdate);
    } else if (isSimEventFrame(frame)) {
      this.handlers?.onSimEvent(frame as unknown as SimEvent);
    } else if (isWorldUpdateFrame(frame)) {
      this.handlers?.onWorldUpdate(frame as unknown as WorldUpdate);
    } else if (isAreaOfInterestFrame(frame)) {
      this.handlers?.onAreaOfInterest(frame as unknown as AreaOfInterest);
    } else if (isUtteranceFrame(frame)) {
      this.handleUtterance(frame);
    } else if (isConfigFrame(frame)) {
      this.handleConfig(frame);
    } else if (frame.type === 'pong') {
      // pong — no action needed
    }
    // Unknown frames silently ignored per contract
  }

  private handleConnected(frame: ConnectedFrame): void {
    const sessionBootstrap = frame.experience_session_bootstrap;
    // experience_session_bootstrap.session_id is the staffed run id and is the
    // authority here: no fallback to a room instance id for this source. A
    // legacy client_bootstrap frame carries no session_id of its own, so that
    // path falls back to its room instance id (see normalizeBootstrapBindings).
    const bindings =
      normalizeBootstrapBindings(sessionBootstrap) ??
      normalizeBootstrapBindings(frame.client_bootstrap, { fallbackSessionIdToRoom: true });

    if (!bindings) {
      const status = isRecord(sessionBootstrap) ? text(sessionBootstrap.status) : undefined;
      if (status === 'config_unavailable' || status === 'location_required') {
        const experienceKey =
          text((sessionBootstrap as Record<string, unknown>).experience_key) ??
          text((sessionBootstrap as Record<string, unknown>).experienceKey);
        const displayExperienceKey = experienceKey ?? this.currentExperienceId;
        const message =
          status === 'config_unavailable'
            ? `This server has no ${displayExperienceKey} configuration yet.`
            : 'The experience needs your location before it can start.';
        this.handlers?.onError({
          type: 'error',
          message,
          code: status === 'config_unavailable' ? 'bootstrap_config_unavailable' : 'bootstrap_location_required',
          detail: `experience_session_bootstrap.status=${status} experience_key=${experienceKey}`,
        });
        return;
      }

      if (status && !READY_BOOTSTRAP_STATUSES.has(status)) {
        const experienceKey =
          text((sessionBootstrap as Record<string, unknown>).experience_key) ??
          text((sessionBootstrap as Record<string, unknown>).experienceKey);
        this.handlers?.onError({
          type: 'error',
          message: `The experience is not ready: ${status.replace(/_/g, ' ')}.`,
          code: `bootstrap_${status}`,
          detail: `experience_session_bootstrap.status=${status} experience_key=${experienceKey}`,
        });
        return;
      }

      this.handlers?.onError({
        type: 'error',
        message: 'Connected to Gaia. Waiting for experience bootstrap.',
        code: 'missing_client_bootstrap',
        detail: 'connected.client_bootstrap missing or incomplete',
      });
      return;
    }

    this.currentBindings = bindings;
    this.handlers?.onConnected(bindings);
  }

  private handleActionResponse(frame: ActionResponseFrame): void {
    const response: ActionResponse = {
      type: frame.type,
      action: frame.action,
      success: frame.success,
      message: frame.message,
      request_id: frame.request_id,
      projection: frame.projection
        ? {
            plain_text: frame.projection.plain_text,
            ...(frame.projection as Record<string, unknown>),
          }
        : undefined,
      state_changes: frame.state_changes,
      metadata: frame.metadata,
      mission_update: (frame as unknown as Record<string, unknown>).mission_update as
        | MissionUpdate
        | undefined,
    };
    this.handlers?.onActionResponse(response);
  }

  private handleUtterance(frame: UtteranceFrame): void {
    const utterance: Utterance = {
      type: 'experience.utterance',
      sessionId: frame.session_id,
      utteranceId: frame.utterance_id,
      sequence: frame.sequence,
      speaker: {
        actorInstanceId: frame.speaker.actor_instance_id,
        actorTemplateId: frame.speaker.actor_template_id,
        role: frame.speaker.role,
      },
      text: frame.text,
      priority: frame.priority,
      disposition: frame.disposition
        ? {
            mode: frame.disposition.mode,
            interruptsUtteranceId: frame.disposition.interrupts_utterance_id,
          }
        : undefined,
      source: frame.source
        ? {
            kind: frame.source.kind,
            missionId: frame.source.mission_id,
            stepId: frame.source.step_id,
            lineId: frame.source.line_id,
            response: normalizeUtteranceResponse(frame.source.response),
          }
        : undefined,
      presentationDirectives: parsePresentationDirectives(
        (frame as unknown as Record<string, unknown>)[PRESENTATION_DIRECTIVES_FIELD],
        console.warn,
        this.allowedPresentationMethods
      ),
      stepTitle: text(frame.step_title),
    };
    this.handlers?.onUtterance?.(utterance);
  }

  private handleConfig(frame: ConfigFrame): void {
    // The session's allowed display methods (missionPresentationVocabulary v2); null means use the client repertoire alone.
    this.allowedPresentationMethods = allowedMethodsFromVocabulary(frame.missionPresentationVocabulary);
    const config: ExperienceConfig = {
      sessionId: frame.session_id,
      characterId: frame.characterId ?? frame.character_id,
      publishedVersionId: frame.published_version_id ?? frame.publishedVersionId,
      experienceKey: frame.experienceKey ?? frame.experience_key,
      stepTitles: stepTitlesFromNodes(frame.nodes),
    };
    this.handlers?.onConfig?.(config);
  }

  private handleError(frame: ErrorFrame): void {
    this.handlers?.onError(frame);
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => this.sendPing(), PING_INTERVAL);
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    const delay = Math.min(
      RECONNECT_BASE_DELAY * Math.pow(2, this.reconnectAttempts),
      RECONNECT_MAX_DELAY
    );
    this.reconnectAttempts++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.doConnect();
    }, delay);
  }
}

function toWireActionFrame(intent: TalkActionIntent | ExperienceActionIntent): Record<string, unknown> {
  const actionIntent = intent as ExperienceActionIntent;
  return {
    type: 'action',
    action: intent.action,
    ...(intent.action === 'talk' ? { message: (intent as TalkActionIntent).message } : {}),
    room_instance_id: intent.bindings.roomInstanceId,
    character_instance_id: intent.bindings.characterInstanceId ?? '',
    ...(intent.bindings.participantActorId ? { actor_instance_id: intent.bindings.participantActorId } : {}),
    request_id: intent.requestId,
    timestamp: intent.timestamp ?? Date.now(),
    ...(actionIntent.payload ? { payload: actionIntent.payload } : {}),
    ...(actionIntent.target !== undefined ? { target: actionIntent.target } : {}),
    ...(actionIntent.context !== undefined ? { context: actionIntent.context } : {}),
  };
}

function normalizeBootstrapBindings(
  raw: unknown,
  options: { fallbackSessionIdToRoom?: boolean } = {}
): BootstrapBindings | null {
  if (!isRecord(raw)) return null;

  const experience = asRecord(raw.experience);
  const room = asRecord(raw.room);
  const roomInstanceBinding = asRecord(raw.room_instance_binding);
  const activeActor = asRecord(raw.activeActor) ?? asRecord(raw.active_actor);
  const character = asRecord(raw.character);
  const participantActor = asRecord(raw.participantActor) ?? asRecord(raw.participant_actor);
  const surfaceBinding = asRecord(raw.surface_binding);

  const experienceId =
    text(experience?.id) ??
    text(experience?.experience_id) ??
    text(raw.experience_id) ??
    text(raw.experience_key) ??
    text(raw.experienceKey);
  const experienceName =
    text(experience?.displayName) ??
    text(experience?.display_name) ??
    text(experience?.experience_id) ??
    text(raw.experience_key) ??
    text(raw.experienceKey) ??
    experienceId;
  const roomInstanceId =
    text(room?.instanceId) ??
    text(room?.room_instance_id) ??
    text(roomInstanceBinding?.room_instance_id) ??
    text(raw.roomInstanceId) ??
    text(raw.room_instance_id);
  const activeActorId =
    text(activeActor?.id) ??
    text(activeActor?.actor_instance_id) ??
    text(activeActor?.character_instance_id);
  const activeActorName =
    text(activeActor?.displayName) ??
    text(activeActor?.display_name) ??
    text(activeActor?.character_id) ??
    activeActorId;

  // The staffed run id (experience_session_bootstrap.session_id) is the
  // authority. Only a legacy client_bootstrap frame, which carries no
  // session_id of its own, falls back to its room instance id.
  const sessionId =
    text(raw.session_id) ??
    text(raw.sessionId) ??
    (options.fallbackSessionIdToRoom ? roomInstanceId : undefined);

  if (!experienceId || !experienceName || !sessionId) {
    return null;
  }

  return {
    sessionId,
    experienceId,
    experienceName,
    publishedVersionId: text(raw.published_version_id) ?? text(raw.publishedVersionId),
    roomInstanceId,
    activeActorId,
    activeActorName,
    activeActorRole: activeActor
      ? text(activeActor.roleLabel) ??
        text(activeActor.role_label) ??
        firstText(activeActor.mission_actor_roles) ??
        text(activeActor.slot) ??
        'Guide'
      : undefined,
    characterId:
      text(activeActor?.characterId) ??
      text(activeActor?.character_id) ??
      text(character?.characterId) ??
      text(character?.character_id),
    characterInstanceId:
      text(character?.instanceId) ??
      text(character?.character_instance_id) ??
      text(activeActor?.character_instance_id),
    participantActorId:
      text(participantActor?.instanceId) ??
      text(participantActor?.actor_instance_id) ??
      text(raw.participant_actor_id),
    legalActions: stringList(raw.legalActions) ?? stringList(raw.legal_actions),
    missionProjection: normalizeMissionProjection(raw.missionProjection ?? raw.mission_projection ?? raw.mission),
    themeKey: text(experience?.themeKey) ?? text(experience?.theme_key) ?? text(surfaceBinding?.theme_key),
    presentCharacters: normalizePresentCharacters(raw.presentCharacters ?? raw.present_characters),
  };
}

/**
 * source.response is authoritative when present and well-formed. Anything
 * that does not match the declared shape (awaiting missing/non-boolean,
 * accepts missing/not a string array) is treated as absent per contract rule
 * 1, so it never overrides the client's current answer affordances. Logged
 * once so a malformed step definition does not spam the console.
 */
function normalizeUtteranceResponse(raw: unknown): UtteranceResponse | undefined {
  if (!isRecord(raw)) return undefined;
  const awaiting = raw.awaiting;
  const accepts = raw.accepts;
  const stepId = raw.step_id;
  const valid =
    typeof awaiting === 'boolean' &&
    Array.isArray(accepts) &&
    accepts.every((item): item is string => typeof item === 'string') &&
    (stepId === undefined || typeof stepId === 'string');
  if (!valid) {
    if (!hasWarnedAboutMalformedResponse) {
      hasWarnedAboutMalformedResponse = true;
      console.warn('GaiaExperienceWebSocket: malformed experience.utterance source.response, treating as absent', raw);
    }
    return undefined;
  }
  return {
    awaiting,
    stepId: typeof stepId === 'string' ? stepId : undefined,
    accepts: accepts as string[],
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return isRecord(value) ? value : undefined;
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function firstText(value: unknown): string | undefined {
  return Array.isArray(value)
    ? value.find((item): item is string => typeof item === 'string' && item.length > 0)
    : undefined;
}

function stringList(value: unknown): string[] | undefined {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : undefined;
}

function stringMap(value: unknown): Record<string, string> | undefined {
  const raw = asRecord(value);
  if (!raw) return undefined;
  const result = Object.fromEntries(
    Object.entries(raw).filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].trim().length > 0)
  );
  return Object.keys(result).length > 0 ? result : undefined;
}

function normalizeMissionProjection(value: unknown): import('../../../ports/ExperiencePort').MissionProjection | undefined {
  const raw = asRecord(value);
  if (!raw) return undefined;
  return {
    missionId: text(raw.missionId) ?? text(raw.mission_id),
    missionInstanceId: text(raw.missionInstanceId) ?? text(raw.mission_instance_id),
    title: text(raw.title) ?? text(raw.display_name) ?? text(raw.name),
    phase: text(raw.phase),
    objective: text(raw.objective) ?? text(raw.objective_text),
    brief: text(raw.brief) ?? text(raw.brief_text) ?? text(raw.description),
    statusText: text(raw.statusText) ?? text(raw.status_text),
    outcomeText: text(raw.outcomeText) ?? text(raw.outcome_text),
    worldMark: text(raw.worldMark) ?? text(raw.world_mark) ?? text(raw.continuity_note),
    legalActions: stringList(raw.legalActions) ?? stringList(raw.legal_actions),
    actionLabels: stringMap(raw.actionLabels) ?? stringMap(raw.action_labels) ?? stringMap(asRecord(raw.presentation)?.action_labels),
    presentation: asRecord(raw.presentation),
  };
}

function normalizePresentCharacters(value: unknown): CharacterPresenceProjection[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.map(normalizePresentCharacter).filter((item): item is CharacterPresenceProjection => item !== null);
}

function normalizePresentCharacter(value: unknown): CharacterPresenceProjection | null {
  const raw = asRecord(value);
  if (!raw) return null;

  const actorInstanceId = text(raw.actorInstanceId) ?? text(raw.actor_instance_id);
  const displayName = text(raw.displayName) ?? text(raw.display_name) ?? actorInstanceId;
  if (!actorInstanceId || !displayName) return null;

  const expression = asRecord(raw.expression);
  const voice = asRecord(raw.voice);
  const capabilities = asRecord(raw.capabilities);

  return {
    actorInstanceId,
    characterId: text(raw.characterId) ?? text(raw.character_id),
    characterInstanceId: text(raw.characterInstanceId) ?? text(raw.character_instance_id),
    characterTemplateId: text(raw.characterTemplateId) ?? text(raw.character_template_id),
    displayName,
    roleLabel: text(raw.roleLabel) ?? text(raw.role_label),
    presenceState: normalizePresenceState(text(raw.presenceState) ?? text(raw.presence_state)),
    conversationRole: normalizeConversationRole(text(raw.conversationRole) ?? text(raw.conversation_role)),
    expression: {
      expressionId: text(expression?.expressionId) ?? text(expression?.expression_id) ?? 'neutral',
      toneLabel: text(expression?.toneLabel) ?? text(expression?.tone_label),
      visualToken: text(expression?.visualToken) ?? text(expression?.visual_token),
      motionToken: text(expression?.motionToken) ?? text(expression?.motion_token),
      textAlt: text(expression?.textAlt) ?? text(expression?.text_alt) ?? `${displayName} has no projected expression.`,
      priority: numberValue(expression?.priority),
    },
    voice: voice
      ? {
          voiceProfileId: text(voice.voiceProfileId) ?? text(voice.voice_profile_id),
          policy: normalizeVoicePolicy(text(voice.policy)),
          playbackHint: normalizePlaybackHint(text(voice.playbackHint) ?? text(voice.playback_hint)),
        }
      : undefined,
    legalActions: stringList(raw.legalActions) ?? stringList(raw.legal_actions) ?? [],
    capabilities: {
      textTurn: booleanValue(capabilities?.textTurn ?? capabilities?.text_turn),
      voiceReply: booleanValue(capabilities?.voiceReply ?? capabilities?.voice_reply),
      visualExpression: booleanValue(capabilities?.visualExpression ?? capabilities?.visual_expression),
      participantSelectableExpression: booleanValue(
        capabilities?.participantSelectableExpression ?? capabilities?.participant_selectable_expression
      ),
    },
  };
}

function normalizePresenceState(value: string | undefined): CharacterPresenceState {
  return value === 'present' || value === 'remote' || value === 'busy' || value === 'unavailable' || value === 'unknown'
    ? value
    : 'unknown';
}

function normalizeConversationRole(value: string | undefined): CharacterConversationRole {
  return value === 'primary_speaker' || value === 'listener' || value === 'observer' || value === 'system' || value === 'unknown'
    ? value
    : 'unknown';
}

function normalizeVoicePolicy(value: string | undefined): 'server_derived' | 'server_selectable' | 'unavailable' {
  return value === 'server_derived' || value === 'server_selectable' || value === 'unavailable'
    ? value
    : 'unavailable';
}

function normalizePlaybackHint(
  value: string | undefined
): 'spoken_reply_available' | 'text_only' | 'participant_choice_available' | undefined {
  return value === 'spoken_reply_available' || value === 'text_only' || value === 'participant_choice_available'
    ? value
    : undefined;
}

function booleanValue(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function numberValue(value: unknown): number | undefined {
  return typeof value === 'number' ? value : undefined;
}

/** Authored step display names keyed by step id, from experience.config nodes; entries without both are skipped. */
function stepTitlesFromNodes(nodes: unknown): Record<string, string> | undefined {
  if (!Array.isArray(nodes)) return undefined;
  const titles: Record<string, string> = {};
  for (const node of nodes) {
    if (typeof node !== 'object' || node === null) continue;
    const { nodeId, displayName } = node as Record<string, unknown>;
    if (typeof nodeId === 'string' && typeof displayName === 'string' && displayName.trim()) {
      titles[nodeId] = displayName.trim();
    }
  }
  return Object.keys(titles).length > 0 ? titles : undefined;
}
